export type ChartOptions = Record<string, unknown>;

const DEFAULT_GRID = { top: 8, right: 8, bottom: 8, left: 8, containLabel: true };

// One scroll-legend row (theme: 10px offset + 16px row + 10px gap) kept clear of the plot.
const LEGEND_GRID_RESERVE_PX = 36;

// Unbounded category labels plus containLabel would shrink the plot to nothing.
const TRUNCATE_AXIS_LABEL = { overflow: 'truncate', width: 150 };

const CARTESIAN_SERIES_TYPES = new Set([
  'line',
  'bar',
  'scatter',
  'effectScatter',
  'heatmap',
  'candlestick',
  'boxplot',
]);

export const isCartesianOptions = (options: ChartOptions): boolean => {
  const series = options['series'];
  const list = Array.isArray(series) ? series : series ? [series] : [];
  return list.some(
    s =>
      typeof s === 'object' &&
      s !== null &&
      CARTESIAN_SERIES_TYPES.has((<{ type?: string }>s).type ?? ''),
  );
};

const clampCategoryAxisLabels = (axis: unknown): unknown => {
  if (Array.isArray(axis)) return axis.map(clampCategoryAxisLabels);
  if (typeof axis !== 'object' || axis === null) return axis;
  const record = <ChartOptions>axis;
  if (record['type'] !== 'category') return axis;
  return {
    ...record,
    axisLabel: { ...(<ChartOptions>(record['axisLabel'] ?? {})), ...TRUNCATE_AXIS_LABEL },
  };
};

const isVisibleLegend = (legend: unknown): legend is ChartOptions =>
  typeof legend === 'object' &&
  legend !== null &&
  !Array.isArray(legend) &&
  (<ChartOptions>legend)['show'] !== false;

const withScrollLegend = (options: ChartOptions): ChartOptions =>
  isVisibleLegend(options['legend'])
    ? { ...options, legend: { type: 'scroll', ...options['legend'] } }
    : options;

// ECharts tooltips don't wrap by default, so a long single-line name outgrows the ~360px floating-panel chart and confine alone can't help.
const TOOLTIP_WRAP_CSS = 'max-width: 320px; white-space: normal; word-break: break-word;';

const withConfinedTooltip = (options: ChartOptions): ChartOptions => {
  const tooltip = options['tooltip'];
  if (typeof tooltip !== 'object' || tooltip === null) return options;
  return {
    ...options,
    tooltip: { ...(<ChartOptions>tooltip), confine: true, extraCssText: TOOLTIP_WRAP_CSS },
  };
};

const presenterGrid = (legend: unknown): ChartOptions => {
  if (!isVisibleLegend(legend)) return { ...DEFAULT_GRID };
  return 'bottom' in legend && !('top' in legend)
    ? { ...DEFAULT_GRID, bottom: LEGEND_GRID_RESERVE_PX }
    : { ...DEFAULT_GRID, top: LEGEND_GRID_RESERVE_PX };
};

const hidePieSliceLabels = (series: unknown): unknown => {
  if (Array.isArray(series)) return series.map(hidePieSliceLabels);
  if (typeof series !== 'object' || series === null) return series;
  const record = <ChartOptions>series;
  if (record['type'] !== 'pie') return series;
  return {
    ...record,
    label: { ...(<ChartOptions>(record['label'] ?? {})), show: false },
    labelLine: { ...(<ChartOptions>(record['labelLine'] ?? {})), show: false },
  };
};

const normalizeCartesianOptions = (raw: ChartOptions): ChartOptions => {
  const options: ChartOptions = { ...raw, grid: presenterGrid(raw['legend']) };
  if ('xAxis' in raw) options['xAxis'] = clampCategoryAxisLabels(raw['xAxis']);
  if ('yAxis' in raw) options['yAxis'] = clampCategoryAxisLabels(raw['yAxis']);
  return options;
};

const normalizeNonCartesianOptions = (raw: ChartOptions): ChartOptions => {
  const options: ChartOptions = { ...raw, xAxis: { show: false }, yAxis: { show: false } };
  if ('series' in raw) options['series'] = hidePieSliceLabels(raw['series']);
  return options;
};

// Layout is presenter-owned and clamped (grid, label overflow, tooltip confinement); data, series types, titles and legend content are model-owned.
export const normalizeChartOptions = (raw: ChartOptions): ChartOptions =>
  withConfinedTooltip(
    withScrollLegend(
      isCartesianOptions(raw) ? normalizeCartesianOptions(raw) : normalizeNonCartesianOptions(raw),
    ),
  );
